/**
 * GenerationProgress — staged progress indicator for long-running OnePass
 * operations (Intelligence Report generation, blueprint generation).
 *
 * Progress is HONEST: each stage is only marked complete when its underlying
 * request/phase actually finishes (useStagedProgress.completeStage). Between
 * real milestones the bar trickles gently toward — but never past — the next
 * milestone, so the UI feels alive without claiming unfinished work is done.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const TRICKLE_MS = 350;

/**
 * Controller hook. Statuses: idle → running → success | error.
 * completeStage(i) marks stage i done (call it when that phase REALLY
 * finished); progress jumps to the reached milestone and trickles toward the
 * next one. finish() = 100%, fail() halts the bar where it is.
 */
export function useStagedProgress(stageCount) {
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [doneStages, setDoneStages] = useState([]);
  const [message, setMessage] = useState("");
  const doneRef = useRef(new Set());
  const timerRef = useRef(null);

  const stopTrickle = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTrickle = useCallback(() => {
    stopTrickle();
    timerRef.current = setInterval(() => {
      setProgress((p) => {
        const doneCount = doneRef.current.size;
        // Never trickle past (next milestone - 4%): only real completions
        // may cross a milestone.
        const cap = Math.max(0, ((doneCount + 1) / stageCount) * 100 - 4);
        if (p >= cap) return p;
        return Math.min(cap, p + Math.max(0.4, (cap - p) * 0.08));
      });
    }, TRICKLE_MS);
  }, [stageCount, stopTrickle]);

  const begin = useCallback(
    (msg = "") => {
      doneRef.current = new Set();
      setDoneStages([]);
      setStatus("running");
      setProgress(2);
      setMessage(msg);
      startTrickle();
    },
    [startTrickle],
  );

  const completeStage = useCallback(
    (index, msg) => {
      doneRef.current.add(index);
      setDoneStages(Array.from(doneRef.current));
      const doneCount = doneRef.current.size;
      setProgress((p) => Math.max(p, Math.round((doneCount / stageCount) * 100)));
      if (msg !== undefined) setMessage(msg);
    },
    [stageCount],
  );

  const finish = useCallback(
    (msg) => {
      stopTrickle();
      doneRef.current = new Set(Array.from({ length: stageCount }, (_, i) => i));
      setDoneStages(Array.from(doneRef.current));
      setProgress(100);
      setStatus("success");
      if (msg !== undefined) setMessage(msg);
    },
    [stageCount, stopTrickle],
  );

  const fail = useCallback(
    (msg) => {
      stopTrickle();
      setStatus("error");
      if (msg !== undefined) setMessage(msg);
    },
    [stopTrickle],
  );

  const reset = useCallback(() => {
    stopTrickle();
    doneRef.current = new Set();
    setDoneStages([]);
    setStatus("idle");
    setProgress(0);
    setMessage("");
  }, [stopTrickle]);

  useEffect(() => stopTrickle, [stopTrickle]);

  return { status, progress, doneStages, message, begin, completeStage, finish, fail, reset };
}

function StageRow({ label, done, active, failed }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        color: done
          ? "success.main"
          : failed
            ? "error.main"
            : active
              ? "primary.main"
              : "text.secondary",
      }}
    >
      {done ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} color="success" data-testid="stage-done" />
      ) : failed ? (
        <ErrorOutlineIcon sx={{ fontSize: 18 }} color="error" data-testid="stage-failed" />
      ) : active ? (
        <CircularProgress size={14} thickness={5} data-testid="stage-active" />
      ) : (
        <FiberManualRecordIcon sx={{ fontSize: 9 }} data-testid="stage-pending" />
      )}
      <Typography variant="body2" sx={{ fontWeight: active || done ? 600 : 500 }}>
        {done ? `${label} — completed` : label}
      </Typography>
    </Box>
  );
}

/**
 * Props:
 *  - title:      heading, e.g. "Intelligence Report Generation"
 *  - stages:     array of stage labels (same length as the controller's stageCount)
 *  - controller: the object returned by useStagedProgress
 *  - onRetry:    optional; when provided and status === "error", a Retry button shows
 */
export default function GenerationProgress({ title, stages, controller, onRetry, sx }) {
  const { status, progress, doneStages, message } = controller;
  if (status === "idle") return null;

  const doneSet = new Set(doneStages);
  const failed = status === "error";
  const succeeded = status === "success";
  const activeIndex = failed || succeeded ? -1 : stages.findIndex((_, i) => !doneSet.has(i));
  const value = Math.min(100, Math.round(progress));

  return (
    <Paper
      variant="outlined"
      role="status"
      aria-label={title}
      sx={{
        p: 2,
        borderColor: failed ? "error.light" : succeeded ? "success.light" : "primary.light",
        bgcolor: failed ? "#fffafa" : succeeded ? "#f6fff8" : "#f8f9ff",
        borderRadius: 2,
        ...sx,
      }}
    >
      <Stack spacing={1.25}>
        <Typography variant="subtitle2" fontWeight={700} color={failed ? "error.main" : "primary"}>
          {title}
        </Typography>

        <Stack spacing={0.75}>
          {stages.map((label, i) => (
            <StageRow
              key={label}
              label={label}
              done={doneSet.has(i)}
              active={i === activeIndex}
              failed={failed && i === stages.findIndex((_, j) => !doneSet.has(j))}
            />
          ))}
        </Stack>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              data-testid="generation-progress-bar"
              variant="determinate"
              value={value}
              color={failed ? "error" : succeeded ? "success" : "primary"}
              sx={{ height: 8, borderRadius: 999 }}
            />
          </Box>
          <Typography variant="caption" fontWeight={700} sx={{ minWidth: 34, textAlign: "right" }}>
            {value}%
          </Typography>
        </Box>

        {/* aria-live so screen readers hear status changes without color cues */}
        <Box aria-live="polite">
          {message && (
            <Typography
              variant="caption"
              color={failed ? "error.main" : succeeded ? "success.main" : "text.secondary"}
            >
              {message}
            </Typography>
          )}
          {!message && !failed && !succeeded && (
            <Typography variant="caption" color="text.secondary">
              Please wait — this is still working.
            </Typography>
          )}
        </Box>

        {failed && onRetry && (
          <Box>
            <Button size="small" variant="outlined" color="error" onClick={onRetry}>
              Retry
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
